"use client";

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export const SIDE_NAV_RAIL_TIP_DELAY_MS = 1000;

export function SideNavRailTooltip({
  label,
  show,
  children,
}: {
  label: string;
  show: boolean;
  children: ReactNode;
}) {
  const tipId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  if (!show) {
    return children;
  }

  const clearTimer = () => {
    window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
  };

  const close = () => {
    clearTimer();
    setOpen(false);
  };

  const scheduleOpen = () => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      const node = anchorRef.current;
      if (!node) {
        return;
      }
      const rect = node.getBoundingClientRect();
      setCoords({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });
      setOpen(true);
    }, SIDE_NAV_RAIL_TIP_DELAY_MS);
  };

  return (
    <>
      <span
        ref={anchorRef}
        className="side-nav-rail-tip-anchor"
        onMouseEnter={scheduleOpen}
        onMouseLeave={close}
        onFocusCapture={scheduleOpen}
        onBlurCapture={close}
      >
        {children}
      </span>
      {open
        ? createPortal(
            <span
              id={tipId}
              className="side-nav-rail-tip cds--white"
              role="tooltip"
              style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
              }}
            >
              {label}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
